import { C } from '../styles';
import BlogLayout, { P, H2b, UL, LI } from './BlogLayout';
import { posts } from './posts';

const meta = posts.find((p) => p.slug === 'sheet-metal-vs-cnc-machining');

const comparisons = [
  { factor: 'Cost per part', sheet: 'Lower, especially at volume', cnc: 'Higher — more machine time per part' },
  { factor: 'Speed', sheet: 'Fast — cutting and forming are quick operations', cnc: 'Slower — cutting away material takes more time' },
  { factor: 'Precision', sheet: 'Good, but limited by bending tolerances', cnc: 'Excellent — tight tolerances on every feature' },
  { factor: 'Geometry', sheet: 'Best for flat, formed, and folded shapes', cnc: 'Best for solid, 3D, and internal features' },
  { factor: 'Material waste', sheet: 'Low — parts are cut from flat sheet', cnc: 'Higher — material is machined away from solid stock' },
  { factor: 'Strength', sheet: 'Good for its weight, especially when formed or ribbed', cnc: 'Excellent — solid stock retains full material strength' },
];

function ComparisonRow({ factor, sheet, cnc }) {
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: '140px 1fr 1fr', gap: 14,
      padding: '14px 0', borderBottom: `1px solid ${C.bdr}`,
    }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: C.txt }}>{factor}</div>
      <div style={{ fontSize: 13, color: C.mut, lineHeight: 1.6 }}>{sheet}</div>
      <div style={{ fontSize: 13, color: C.mut, lineHeight: 1.6 }}>{cnc}</div>
    </div>
  );
}

export default function SheetMetalVsCNC() {
  return (
    <BlogLayout title={meta.title} description={meta.description}>
      <P>
        Sheet metal fabrication and CNC machining are the two most common ways to turn a metal design into a physical part — and they are often confused
        because both can produce a finished, functional component. The right one depends entirely on the shape of your part and what it needs to do.
      </P>

      <H2b>What is sheet metal fabrication?</H2b>
      <P>
        Sheet metal fabrication starts with a flat sheet of metal, which is cut (typically by laser or punch) and then bent or formed into shape. It is the
        process behind enclosures, brackets, panels, shelving, and anything built from folded flat stock.
      </P>

      <H2b>What is CNC machining?</H2b>
      <P>
        CNC machining starts with a solid block or bar of material and removes material — by milling, drilling, or turning — until the final shape remains.
        It is the process behind parts with precise mechanical features: threaded holes, tight-tolerance surfaces, pockets, and complex 3D geometry.
      </P>

      <H2b>Head-to-head comparison</H2b>
      <div style={{ marginBottom: 20 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr 1fr', gap: 14, paddingBottom: 10, borderBottom: `1px solid ${C.bdr2}` }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: C.dim, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Factor</div>
          <div style={{ fontSize: 11, fontWeight: 600, color: C.navy, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Sheet metal</div>
          <div style={{ fontSize: 11, fontWeight: 600, color: C.orange, letterSpacing: '0.08em', textTransform: 'uppercase' }}>CNC machining</div>
        </div>
        {comparisons.map((row) => <ComparisonRow key={row.factor} {...row} />)}
      </div>

      <H2b>Decision guide: when to use which</H2b>
      <P><strong>Choose sheet metal fabrication if:</strong></P>
      <UL>
        <LI>Your part is made from flat, formed, or folded shapes — enclosures, brackets, panels</LI>
        <LI>You need parts quickly and at lower cost, especially at moderate-to-high volume</LI>
        <LI>Tight internal tolerances are not critical to the part's function</LI>
      </UL>
      <P><strong>Choose CNC machining if:</strong></P>
      <UL>
        <LI>Your part has precise mechanical features — threaded holes, tight-fit surfaces, internal pockets</LI>
        <LI>It needs to be machined from solid stock for strength or geometry that sheet metal cannot form</LI>
        <LI>Tolerances matter more than per-part cost</LI>
      </UL>
      <P>
        Many products actually use both — a machined component paired with a sheet metal enclosure, for example. If you are not sure which your part needs,
        describing the part and its use case is usually enough for a fabricator to tell you which process fits.
      </P>
    </BlogLayout>
  );
}
